export enum ErrorCodes {
  UserDisabled = 'userDisabled',
  UnverifiedEmail = 'unverifiedEmail',
  EmailPasswordAccountExists = 'emailExists',
  AccountExists = 'accountExists',
  InvalidEmailOrPassword = 'invalidEmailOrPassword',
  NotAllowed = 'notAllowed',
  WeakPassword = 'weakPassword',
  GenericAuthFailure = 'genericAuthFailure',
  InviteExpired = 'inviteExpired',
  InvalidInvite = 'invalidInvite',
  LinkExpired = 'linkExpired',
  InvalidLink = 'invalidLink',
  NetworkFailure = 'networkFailure',
  IntermittentError = 'tryAgain',
  CredentialExistWithDifferentProvider = 'credentialExistWithDifferentProvider',
  FederatedAuthNetworkBlocked = 'federatedAuthNetworkBlocked',
  FederatedAuthScriptBlocked = 'federatedAuthScriptBlocked',
}

export class ErrorMessage {
  title?: string
  content?: string
}

export const toMessage = (errCode: string): ErrorMessage => {
  const errorMessage = new ErrorMessage()
  switch (errCode) {
    case ErrorCodes.UserDisabled:
      errorMessage.content = `User was disabled.`
      break
    case ErrorCodes.NotAllowed:
      errorMessage.content = `Your account does not have access to this organization.`
      break
    case ErrorCodes.InvalidEmailOrPassword:
      errorMessage.content = `Invalid email or password.`
      break
    case ErrorCodes.EmailPasswordAccountExists:
      errorMessage.content = `You already have an account, enter your existing credentials and log in instead.`
      break
    case ErrorCodes.AccountExists:
      errorMessage.content = `You already have an account, log in to your account.`
      break
    case ErrorCodes.WeakPassword:
      errorMessage.content = `Password is too weak.`
      break
    case ErrorCodes.GenericAuthFailure:
      errorMessage.content = `Login failed. Please try again.`
      break
    case ErrorCodes.InviteExpired:
      errorMessage.content = `Invitation has expired. To request a new invitation, contact your admin.`
      break
    case ErrorCodes.InvalidInvite:
      errorMessage.content = `Something went wrong. To request a new invitation, contact your admin.`
      break
    case ErrorCodes.LinkExpired:
      errorMessage.title = `Link expired or already used`
      errorMessage.content = `Log in to continue`
      break
    case ErrorCodes.InvalidLink:
      errorMessage.title = `Something went wrong`
      errorMessage.content = `Are you sure this is the right link? Contact your admin.`
      break
    case ErrorCodes.NetworkFailure:
      errorMessage.content = `Unable to log in. Check firewall and network allowed URL settings, then try again. Learn more: https://www.papercut.com/help/manuals/pocket-hive/plan-and-get-started/system-requirements/`
      break
    case ErrorCodes.IntermittentError:
      errorMessage.content = 'Something went wrong, please try again'
      break
    case ErrorCodes.CredentialExistWithDifferentProvider:
      errorMessage.title = 'Different account type'
      errorMessage.content = `To continue, log in with an account and login method you've used before. <a href="https://www.papercut.com/kb/PaperCutPocketHive/troubleshooting-papercut-pocket-hive-login/#account-linking" target="_blank">Learn more about different account types</a>`
      break
    case ErrorCodes.FederatedAuthNetworkBlocked:
      errorMessage.title = `Your network is blocking login`
      errorMessage.content = `Your network blocked a request required to log in (https://apis.google.com/js/api.js). Check firewall, VPN, and proxy settings, then try again. Learn more: https://www.papercut.com/help/manuals/pocket-hive/plan-and-get-started/system-requirements/`
      break
    case ErrorCodes.FederatedAuthScriptBlocked:
      errorMessage.title = `Your browser is blocking login`
      errorMessage.content = `Your browser blocked a script required to log in (https://apis.google.com/js/api.js). This is usually caused by a browser extension such as an ad or script blocker. Disable your extensions for this site, then try again. Learn more: https://www.papercut.com/help/manuals/pocket-hive/plan-and-get-started/system-requirements/`
      break
    default:
      errorMessage.content = `Unknown error`
  }

  return errorMessage
}

export const errorIs = (error: unknown, target: ErrorCodes): boolean => {
  if (typeof error === 'string') {
    return error === target
  } else if (error instanceof Error) {
    return error.message === target
  }

  return false
}
